import React from "react";
import { Lock, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBilling } from "@/context/BillingContext";

/**
 * Wraps protected content. If the user lacks `module`, renders blurred preview + lock CTA.
 * Otherwise renders children unchanged.
 *
 * P0 fix: while billing is still hydrating on app cold-start (auth not yet
 * settled, or the first /billing/me call hasn't resolved), we render the
 * children behind a neutral spinner overlay instead of the "upgrade" CTA.
 * Otherwise paid users see a fake "feature locked" screen for the ~200ms
 * window before billing arrives, eroding trust in the product.
 */
export default function PlanLock({ module, requiredPlan = "pro", children, label }) {
  const { has, openUpgrade, isHydrating } = useBilling();

  if (isHydrating) {
    return (
      <div className="relative" data-testid={`plan-lock-${module}-loading`}>
        <div className="pointer-events-none select-none filter blur-sm opacity-40">{children}</div>
        <div className="absolute inset-0 flex items-center justify-center bg-white/40">
          <Loader2 className="w-6 h-6 text-primary animate-spin" strokeWidth={1.5} />
        </div>
      </div>
    );
  }

  if (has(module)) return children;

  return (
    <div className="relative" data-testid={`plan-lock-${module}`}>
      <div className="pointer-events-none select-none filter blur-sm opacity-60">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[2px]">
        <div className="bg-white border border-primary rounded-sm p-6 max-w-md text-center shadow-md">
          <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <div className="mt-3 label-uppercase text-primary">Modulo riservato</div>
          <h3 className="font-serif-heading text-2xl mt-1">
            {label || "Funzione disponibile nel piano superiore"}
          </h3>
          <p className="text-sm text-neutral-600 mt-2">
            Passa al piano superiore per sbloccare questa funzionalità.
          </p>
          <Button
            className="mt-4 rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
            onClick={() => openUpgrade({ reason: `module_${module}`, requiredPlan })}
            data-testid={`plan-lock-${module}-cta`}
          >
            <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} /> Scopri i piani
          </Button>
        </div>
      </div>
    </div>
  );
}
